import JSZip from "jszip";
import * as XLSX from "xlsx";

/** Extensiones que acepta el importador. */
export const SUPPORTED_IMPORT_EXTENSIONS = [".csv", ".xlsx", ".xls"] as const;

export type ParsedSheet = {
    /** Nombre de la hoja (para CSV, el que le pone SheetJS). */
    name: string;
    /** Matriz de celdas ya normalizadas a string. Las filas pueden tener largos distintos. */
    rows: string[][];
};

export type ParsedTabularFile = {
    sheets: ParsedSheet[];
    /** Hoja con mas filas: la que se propone por defecto. */
    defaultSheetIndex: number;
};

export class TabularFileError extends Error {
    constructor(message: string) {
        super(message);
        this.name = "TabularFileError";
    }
}

const isCsvName = (fileName: string): boolean => /\.csv$/i.test(fileName.trim());

/**
 * Decodifica un CSV a texto antes de pasarselo a SheetJS.
 *
 * SheetJS autodetecta el delimitador (`,`, `;`, tab, `|`) pero solo cuando recibe un
 * string: si se le entrega el buffer crudo interpreta los bytes como latin1 y las
 * tildes se rompen ("CategorÃ­a"). Por eso aqui se decodifica primero como UTF-8 y,
 * si aparece el caracter de reemplazo, se reintenta como windows-1252, que es lo que
 * suelen exportar los sistemas de facturacion en Windows.
 */
export const decodeCsvBuffer = (buffer: ArrayBuffer): string => {
    const bytes = new Uint8Array(buffer);
    const utf8 = new TextDecoder("utf-8").decode(bytes);

    if (!utf8.includes("�")) {
        return stripBom(utf8);
    }

    try {
        return stripBom(new TextDecoder("windows-1252").decode(bytes));
    } catch {
        // Algunos runtimes no traen la tabla windows-1252; el UTF-8 degradado es mejor que nada.
        return stripBom(utf8);
    }
};

const stripBom = (text: string): string => (text.charCodeAt(0) === 0xfeff ? text.slice(1) : text);

/** Ruta de la tabla de cadenas compartidas dentro del zip de un XLSX. */
const SHARED_STRINGS_PATH = "xl/sharedStrings.xml";

/** Apertura de `<si>` mal formada que SheetJS 0.18.5 no reconoce. */
const MISPLACED_XML_SPACE = '<si xml:space="preserve">';

/**
 * Repara la tabla de cadenas compartidas de un XLSX antes de leerlo.
 *
 * Algunos generadores escriben `xml:space="preserve"` sobre el elemento `<si>` en lugar
 * del `<t>` que lo admite segun el esquema. El parser de SheetJS 0.18.5 solo reconoce
 * `<si>` a secas: las entradas asi escritas quedan vacias y **toda celda que las
 * referencia se lee en blanco**, sin ningun error.
 *
 * En uno de los archivos reales del cliente esto vaciaba 274 de 1814 cadenas, entre
 * ellas el nombre de 225 clientes, que parecian filas sin nombre en el archivo.
 *
 * El atributo se mueve al `<t>`, donde conserva los espacios y SheetJS si lo entiende.
 * Si el archivo no es un zip (por ejemplo un .xls antiguo) o no trae la tabla, se
 * devuelve el original sin tocar.
 */
export const repairSharedStrings = async (buffer: ArrayBuffer): Promise<ArrayBuffer> => {
    try {
        const zip = await JSZip.loadAsync(buffer);
        const entry = zip.file(SHARED_STRINGS_PATH);
        if (!entry) return buffer;

        const sharedStrings = await entry.async("string");
        if (!sharedStrings.includes(MISPLACED_XML_SPACE)) return buffer;

        zip.file(
            SHARED_STRINGS_PATH,
            sharedStrings.split(`${MISPLACED_XML_SPACE}<t>`).join('<si><t xml:space="preserve">')
        );

        return await zip.generateAsync({ type: "arraybuffer" });
    } catch {
        // Formato inesperado: que lo resuelva (o falle) SheetJS con el buffer original.
        return buffer;
    }
};

const toWorkbook = async (fileName: string, buffer: ArrayBuffer): Promise<XLSX.WorkBook> => {
    if (isCsvName(fileName)) {
        return XLSX.read(decodeCsvBuffer(buffer), { type: "string", raw: true });
    }
    return XLSX.read(await repairSharedStrings(buffer), { type: "array" });
};

/**
 * Convierte una hoja en una matriz de strings.
 *
 * `raw: false` hace que SheetJS aplique el formato de la celda en vez de devolver el
 * numero crudo, de modo que un codigo con ceros a la izquierda o un `4530` no se
 * conviertan silenciosamente en otra cosa. `defval: ""` evita huecos en la matriz.
 */
export const sheetToRows = (sheet: XLSX.WorkSheet): string[][] => {
    const rows = XLSX.utils.sheet_to_json<unknown[]>(sheet, {
        header: 1,
        raw: false,
        defval: "",
        blankrows: false,
    });

    return rows.map((row) => (row || []).map((cell) => `${cell ?? ""}`));
};

/**
 * Lee un CSV o un XLSX del navegador y devuelve sus hojas como matrices de strings.
 *
 * No interpreta encabezados ni columnas: de eso se encargan `detectHeaderRow` y
 * `suggestColumnMapping`. Los archivos vienen de sistemas externos, asi que aqui no se
 * asume ninguna estructura.
 */
export const parseTabularFile = async (file: File): Promise<ParsedTabularFile> => {
    const buffer = await file.arrayBuffer();

    let workbook: XLSX.WorkBook;
    try {
        workbook = await toWorkbook(file.name, buffer);
    } catch (error) {
        throw new TabularFileError(
            `No se pudo leer el archivo "${file.name}". Verifique que sea un CSV o un Excel valido.`
        );
    }

    const sheets: ParsedSheet[] = workbook.SheetNames.map((name) => ({
        name,
        rows: sheetToRows(workbook.Sheets[name]),
    })).filter((sheet) => sheet.rows.length > 0);

    if (!sheets.length) {
        throw new TabularFileError(`El archivo "${file.name}" no tiene ninguna fila con datos.`);
    }

    const defaultSheetIndex = sheets.reduce(
        (best, sheet, index) => (sheet.rows.length > sheets[best].rows.length ? index : best),
        0
    );

    return { sheets, defaultSheetIndex };
};
